import { useEffect, useMemo, useState, useCallback } from 'react';
import { useScope } from '@workspace/scope-context';
import { toast } from '@workspace/ui';
import { useApiGatewayAccess } from '@/lib/useApiGatewayAccess';
import { getApiGatewayOpenApiJsonUrl } from '@/lib/api-gateway-management';

import { DeveloperConsoleLayout } from '@/components/developer-console/DeveloperConsoleLayout';
import { EndpointExplorer, type ApiEndpoint, type ApiParameter, type ApiBodyField, type ApiRequestBody, type HttpMethod } from '@/components/developer-console/EndpointExplorer';
import { RequestStudio, type RequestConfig } from '@/components/developer-console/RequestStudio';
import { type ResponseData } from '@/components/developer-console/ResponseLab';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface DeveloperConsoleStateProps {
  title: string;
  description: string;
}

type OpenApiParameterSpec = {
  name?: string;
  in?: string;
  required?: boolean;
  description?: string;
  schema?: {
    default?: unknown;
    type?: string;
    enum?: unknown[];
    format?: string;
  };
};

const tenantScopedCommerceAdminCollectionPaths = new Set([
  '/api/v1/commerce/admin/catalog-publications',
  '/api/v1/commerce/admin/pricebooks',
  '/api/v1/commerce/admin/pricebook-assignments',
  '/api/v1/commerce/admin/catalogs',
  '/api/v1/commerce/admin/commission-plans',
  '/api/v1/commerce/admin/serviceability-zones',
  '/api/v1/commerce/admin/markets',
  '/api/v1/commerce/admin/market-groups',
]);


function normalizeOpenApiParameter(spec: OpenApiParameterSpec): ApiParameter | null {
  if (!spec.name) {
    return null;
  }
  if (spec.in !== 'path' && spec.in !== 'query' && spec.in !== 'header') {
    return null;
  }
  return {
    name: spec.name,
    in: spec.in,
    required: Boolean(spec.required),
    description: spec.description,
    defaultValue:
      spec.schema?.default === undefined || spec.schema?.default === null
        ? undefined
        : String(spec.schema.default),
    type: spec.schema?.type,
    enum: Array.isArray(spec.schema?.enum) ? spec.schema.enum.map(String) : undefined,
    format: spec.schema?.format,
  };
}

function resolveSchema(schema: any, spec: any): any {
  if (!schema) return schema;
  if (schema.$ref && typeof schema.$ref === 'string') {
    const parts = schema.$ref.replace(/^#\//, '').split('/');
    let resolved = spec;
    for (const part of parts) {
      resolved = resolved?.[part];
    }
    return resolveSchema(resolved, spec);
  }
  return schema;
}

function buildRequestBody(operation: any, spec: any): ApiRequestBody | undefined {
  let schema = operation?.requestBody?.content?.['application/json']?.schema;
  schema = resolveSchema(schema, spec);
  
  if (!schema?.properties) return undefined;

  const requiredFields = new Set<string>(Array.isArray(schema.required) ? schema.required : []);

  const fields: ApiBodyField[] = Object.entries(schema.properties).map(([name, propRaw]: [string, any]) => {
    const prop = resolveSchema(propRaw, spec) || {};
    return {
      name,
      type: prop.type ?? 'string',
      required: requiredFields.has(name),
      description: prop.description,
      example: prop.example !== undefined ? String(prop.example) : undefined,
      defaultValue: prop.default !== undefined ? String(prop.default) : undefined,
      enum: Array.isArray(prop.enum) ? prop.enum.map(String) : undefined,
      format: prop.format,
    };
  });

  return {
    description: operation.requestBody?.description,
    required: Boolean(operation.requestBody?.required),
    fields,
  };
}

function buildEndpointParameters(
  path: string,
  method: HttpMethod,
  sharedParameters: OpenApiParameterSpec[] | undefined,
  operationParameters: OpenApiParameterSpec[] | undefined,
): ApiParameter[] {
  const merged = [...(sharedParameters ?? []), ...(operationParameters ?? [])]
    .map(normalizeOpenApiParameter)
    .filter((parameter): parameter is ApiParameter => Boolean(parameter));

  const seen = new Set<string>();
  const deduped = merged.filter(parameter => {
    const key = `${parameter.in}:${parameter.name}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });

  if (
    method === 'GET' &&
    tenantScopedCommerceAdminCollectionPaths.has(path) &&
    !deduped.some(parameter => parameter.in === 'query' && parameter.name === 'catalog_owner_tenant_id')
  ) {
    deduped.unshift({
      name: 'catalog_owner_tenant_id',
      in: 'query',
      required: true,
      description: 'Catalog owner tenant identifier',
    });
  }

  return deduped;
}

function DeveloperConsoleState({ title, description }: DeveloperConsoleStateProps) {
  return (
    <div className="flex items-center justify-center h-full p-6">
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground text-card-foreground">
          The developer console stays inside the portal shell, but access depends on your current tenant context and permissions.
        </CardContent>
      </Card>
    </div>
  );
}

export default function DeveloperConsolePage() {
  const { activeTenant } = useScope();
  const apiGatewayAccess = useApiGatewayAccess();

  const [endpoints, setEndpoints] = useState<ApiEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEndpoint, setSelectedEndpoint] = useState<ApiEndpoint | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [executing, setExecuting] = useState(false);
  const [lastResponse, setLastResponse] = useState<ResponseData | undefined>();

  const fetchOpenApi = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(getApiGatewayOpenApiJsonUrl());
      if (!response.ok) throw new Error('Failed to fetch OpenAPI spec');
      const spec = await response.json();
      
      const parsedEndpoints: ApiEndpoint[] = [];
      if (spec.paths) {
        Object.entries(spec.paths).forEach(([path, pathItem]: [string, any]) => {
          const sharedParameters = Array.isArray(pathItem?.parameters) ? pathItem.parameters : [];
          Object.entries(pathItem).forEach(([method, detail]: [string, any]) => {
            if (['get', 'post', 'put', 'delete', 'patch'].includes(method)) {
              const normalizedMethod = method.toUpperCase() as HttpMethod;
              parsedEndpoints.push({
                path,
                method: normalizedMethod,
                summary: detail.summary || detail.description || 'No description provided',
                tag: detail.tags?.[0] || 'Uncategorized',
                parameters: buildEndpointParameters(
                  path,
                  normalizedMethod,
                  sharedParameters,
                  Array.isArray(detail?.parameters) ? detail.parameters : [],
                ),
                requestBody: buildRequestBody(detail, spec),
              });
            }
          });
        });
      }
      setEndpoints(parsedEndpoints);
    } catch (error) {
      console.error('Failed to parse OpenAPI:', error);
      toast.error('Failed to load API definitions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTenant?.id && apiGatewayAccess.apiGatewayFeatureEnabled && apiGatewayAccess.apiGatewayPermissionGranted) {
      void fetchOpenApi();
    }
  }, [activeTenant?.id, apiGatewayAccess.apiGatewayFeatureEnabled, apiGatewayAccess.apiGatewayPermissionGranted, fetchOpenApi]);

  const handleExecute = useCallback(async (config: RequestConfig) => {
    setExecuting(true);
    const startTime = performance.now();
    
    try {
      // 1. Build interpolated URL
      let finalPath = config.path;
      Object.entries(config.pathParams).forEach(([key, value]) => {
        finalPath = finalPath.replace(`{${key}}`, encodeURIComponent(value));
      });

      // All requests are fed THROUGH the api-gateway via the BFF proxy
      const proxyUrl = new URL(window.location.origin + '/api/v1/api-gateway/proxy' + finalPath);
      Object.entries(config.queryParams).forEach(([key, value]) => {
        if (key && value) proxyUrl.searchParams.append(key, value);
      });

      // 2. Prepare headers
      const fetchHeaders: Record<string, string> = { ...config.headers };
      
      // If using an API key, we should provide it in the correct format.
      // The gateway expects 'Authorization: ApiKey <prefix>.<secret>'
      if (config.authMode === 'key' && config.apiKey) {
        fetchHeaders['Authorization'] = `ApiKey ${config.apiKey}`;
      }

      // 3. Perform request
      const response = await fetch(proxyUrl.toString(), {
        method: config.method,
        headers: fetchHeaders,
        body: config.body ? config.body : undefined,
        credentials: 'include' // Always include session cookie for the BFF proxy itself
      });

      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);
      
      // 4. Parse response
      const contentType = response.headers.get('content-type');
      let body: any;
      let size = '0 B';

      const text = await response.text();
      size = formatBytes(text.length);

      if (contentType?.includes('application/json')) {
        try {
          body = JSON.parse(text);
        } catch {
          body = text;
        }
      } else {
        body = text;
      }

      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((val, key) => {
        responseHeaders[key] = val;
      });

      setLastResponse({
        status: response.status,
        statusText: response.statusText,
        duration,
        size,
        headers: responseHeaders,
        body
      });

      if (response.ok) {
        toast.success(`Request successful (${response.status})`);
      } else {
        toast.error(`Request failed with status ${response.status}`);
      }

    } catch (error) {
      const endTime = performance.now();
      setLastResponse({
        status: 0,
        statusText: 'Error',
        duration: Math.round(endTime - startTime),
        size: '0 B',
        headers: {},
        body: { error: error instanceof Error ? error.message : 'Unknown network error' },
        isError: true
      });
      toast.error('Network request failed');
    } finally {
      setExecuting(false);
    }
  }, []);

  if (!activeTenant?.id) {
    return (
      <DeveloperConsoleState
        title="Select a tenant"
        description="Developer Console access requires an active tenant context in the portal shell."
      />
    );
  }

  if (!apiGatewayAccess.apiGatewayFeatureEnabled) {
    return (
      <DeveloperConsoleState
        title="API Gateway is not enabled"
        description="Ask an administrator to enable the API Gateway tenant feature before opening the developer console here."
      />
    );
  }

  if (!apiGatewayAccess.apiGatewayPermissionGranted) {
    return (
      <DeveloperConsoleState
        title="Access denied"
        description="Your current permissions do not allow you to use the API Gateway developer console."
      />
    );
  }

  return (
    <DeveloperConsoleLayout
      onSearchChange={setSearchQuery}
      explorer={
        <EndpointExplorer 
          endpoints={endpoints} 
          selectedPath={selectedEndpoint?.path}
          selectedMethod={selectedEndpoint?.method}
          onSelect={setSelectedEndpoint}
          searchQuery={searchQuery}
        />
      }
      studio={
        <div className="max-w-4xl mx-auto">
          <RequestStudio
            endpoint={selectedEndpoint}
            onExecute={handleExecute}
            executing={executing}
            activeTenantId={activeTenant.id}
            response={lastResponse}
          />
        </div>
      }
    />
  );
}

function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
